/**
 * WINGMAN UNIFIED SPEECH AUTHORITY (WINGMAN-VOICE-016)
 * 
 * Single authoritative coordinator for ALL application speech output.
 * 
 * INVARIANTS:
 * 1. Exactly ONE speech source can be active at any given time.
 * 2. Unprompted automated announcements (routine counters, background quota refresh,
 *    page load, re-renders, and upgrade promotions) are STRICTLY VISUAL and rejected here.
 *    Do not start a paid session merely to announce a quota denial.
 * 3. Application-facing speech uses Wingman's established voice output path (Web Audio /
 *    neural TTS / VoiceController adapter) rather than default browser speechSynthesis.
 *    If unavailable or unentitled, retain visible content and show a visual explanation.
 * 4. Mutual exclusion in BOTH directions:
 *    - Direction 1: If Wingman Voice AI is already speaking (voiceState === 'speaking'), synthetic speech is suppressed.
 *    - Direction 2: If Wingman Voice AI starts speaking (voiceState transitions to 'speaking'), active synthetic speech is cancelled.
 * 5. Monotonically increasing generation counter shared by speak and cancel:
 *    Cancellation immediately invalidates every callback belonging to the cancelled request.
 * 6. Ownership enforcement: cancel(ownerId) does nothing unless that owner exactly matches
 *    the active owner, including when active speech has no owner.
 * 7. Screen-reader accessibility via #wingman-sr-speech-announcement is preserved.
 */

import { voiceState } from '../../state/voiceState';

export type SpeechSource = 
  | 'READ_ALOUD'            // Explicit user click in NewsPanel or report
  | 'REPORT_COMPLETION'     // ReportVoiceLifecycle single-fire completion
  | 'VOICE_ASSISTANT'       // VoiceController output
  | 'USER_REQUESTED';       // User explicitly asks "What's my quota?"

export interface SpeechOptions {
  source: SpeechSource;
  ownerId?: string;
  metadata?: {
    isUserExplicit?: boolean;
    quotaExceeded?: boolean;
    suppressSpeech?: boolean;
    [key: string]: any;
  };
  rate?: number;
  pitch?: number;
  onEnd?: () => void;
  onError?: (err: any) => void;
}

export interface VoiceOutputAdapter {
  speak: (text: string, options: {
    rate?: number;
    pitch?: number;
    onEnd?: () => void;
    onError?: (err: any) => void;
  }) => Promise<void> | void;
  cancel?: () => void;
  isAvailable?: () => boolean;
}

export class SpeechAuthority {
  private static instance: SpeechAuthority | null = null;
  private speaking = false;
  private activeOwnerId: string | null = null;
  private generation = 0; // Monotonically increasing counter shared by speak and cancel
  private listeners = new Set<(speaking: boolean) => void>();
  private voiceAdapter: VoiceOutputAdapter | null = null;
  private activeAudioSource: AudioBufferSourceNode | null = null;

  private constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.cancel());

      // 🛡️ INVARIANT 4 (Direction 2): When Wingman Voice AI starts speaking, cancel active speech
      voiceState.subscribe((state: string) => {
        if (state === 'speaking' && this.speaking) {
          console.warn('[SpeechAuthority] 🔇 Wingman Voice AI started speaking; cancelling active speech.');
          this.cancel();
        }
      });
    }
  }

  public static getInstance(): SpeechAuthority {
    if (!SpeechAuthority.instance) {
      SpeechAuthority.instance = new SpeechAuthority();
    }
    return SpeechAuthority.instance;
  }

  public setVoiceAdapter(adapter: VoiceOutputAdapter | null): void {
    this.voiceAdapter = adapter;
  }

  public getVoiceAdapter(): VoiceOutputAdapter | null {
    return this.voiceAdapter;
  }

  public getGeneration(): number {
    return this.generation;
  }

  /**
   * Evaluates if text/source/metadata is an unprompted quota or upgrade announcement.
   * Routine quota messages MUST remain visual and never be spoken automatically.
   * Explicitly requested read-aloud (isUserExplicit or USER_REQUESTED) is preserved.
   */
  public isUnpromptedQuotaOrUpgrade(text: string, optionsOrSource: SpeechOptions | SpeechSource): boolean {
    const options: SpeechOptions = typeof optionsOrSource === 'string'
      ? { source: optionsOrSource }
      : optionsOrSource;

    // Explicit user requests and explicitly triggered read-aloud are NEVER suppressed
    if (options.source === 'USER_REQUESTED' || options.metadata?.isUserExplicit === true) {
      return false;
    }

    // Event/Source metadata check
    if (options.metadata?.quotaExceeded || options.metadata?.suppressSpeech) {
      return true;
    }

    const lower = (text || '').toLowerCase();
    const hasQuotaPattern = 
      lower.includes('you have used') ||
      lower.includes('of 250') ||
      lower.includes('upgrade to pro') ||
      lower.includes('upgrade to elite') ||
      lower.includes('plan limit reached') ||
      lower.includes('mission capacity reached') ||
      lower.includes('requests available on your');

    return hasQuotaPattern;
  }

  /**
   * Authoritative speak request.
   * Uses Wingman's established voice output path.
   * Returns true if accepted for playback, false if suppressed or unavailable.
   */
  public speak(text: string, options: SpeechOptions): boolean {
    if (!text || typeof text !== 'string' || !text.trim()) {
      return false;
    }

    // 🛡️ INVARIANT 2: Routine counters and upgrade promotions MUST remain visual.
    // Do not start a paid session merely to announce a quota denial.
    if (this.isUnpromptedQuotaOrUpgrade(text, options)) {
      console.warn('[SpeechAuthority] 🔇 Suppressed unprompted quota/upgrade speech:', text);
      return false;
    }

    // 🛡️ INVARIANT 4 (Direction 1): If Voice AI is already speaking via Web Audio / provider, suppress
    if (typeof window !== 'undefined' && voiceState.state === 'speaking') {
      console.warn('[SpeechAuthority] 🔇 Suppressed speech: Voice AI is actively speaking on Web Audio.');
      return false;
    }

    // Always preserve screen-reader accessibility via standard ARIA live region
    this.updateScreenReaderLiveRegion(text.trim());

    // Invalidate prior speech and cancel active playback if speaking
    if (this.speaking) {
      this.cancelInternal();
    }

    // Advance generation counter (monotonically increasing)
    const currentGen = ++this.generation;
    this.activeOwnerId = options.ownerId || null;

    // Check voice availability and entitlement
    if (!this.isVoicePathAvailable()) {
      this.activeOwnerId = null;
      this.dispatchVoiceUnavailable('Voice output service unavailable', text.trim());
      return false;
    }

    this.speaking = true;
    this.notify(true);

    const handleEnd = () => {
      // 🛡️ INVARIANT 5: Single generation counter invalidation check
      if (this.generation !== currentGen) {
        return;
      }
      this.speaking = false;
      this.activeOwnerId = null;
      this.notify(false);
      options.onEnd?.();
    };

    const handleError = (err: any) => {
      // 🛡️ INVARIANT 5: Single generation counter invalidation check
      if (this.generation !== currentGen) {
        return;
      }
      this.speaking = false;
      this.activeOwnerId = null;
      this.notify(false);
      this.dispatchVoiceUnavailable('Voice playback failure', text.trim(), err);
      options.onError?.(err);
    };

    try {
      this.executeVoiceOutput(text.trim(), options, currentGen, handleEnd, handleError);
      return true;
    } catch (err) {
      if (this.generation === currentGen) {
        this.speaking = false;
        this.activeOwnerId = null;
        this.notify(false);
        this.dispatchVoiceUnavailable('Failed to initiate voice output', text.trim(), err);
      }
      return false;
    }
  }

  /**
   * Cancel all current speech immediately.
   * If ownerId is provided, cancels ONLY if speech is owned by that component.
   * If active speech has no owner, cancel(ownerId) does nothing.
   */
  public cancel(ownerId?: string): void {
    // 🛡️ INVARIANT 6: Strict component ownership check
    if (ownerId !== undefined) {
      if (!this.activeOwnerId || this.activeOwnerId !== ownerId) {
        return;
      }
    }

    this.cancelInternal();
  }

  private cancelInternal(): void {
    // Invalidate every callback belonging to the cancelled request
    this.generation++;
    this.activeOwnerId = null;

    if (this.activeAudioSource) {
      try {
        this.activeAudioSource.stop();
      } catch (e) {}
      this.activeAudioSource = null;
    }

    if (this.voiceAdapter?.cancel) {
      try {
        this.voiceAdapter.cancel();
      } catch (e) {}
    }

    // Legacy synthesis cleanup
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch (e) {}
    }

    if (this.speaking) {
      this.speaking = false;
      this.notify(false);
    }
  }

  public isSpeaking(): boolean {
    return this.speaking;
  }

  public getActiveOwnerId(): string | null {
    return this.activeOwnerId;
  }

  public subscribe(listener: (speaking: boolean) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(speaking: boolean): void {
    this.listeners.forEach(fn => {
      try { fn(speaking); } catch (e) {}
    });
  }

  private isVoicePathAvailable(): boolean {
    if (this.voiceAdapter) {
      return this.voiceAdapter.isAvailable ? this.voiceAdapter.isAvailable() : true;
    }
    if (typeof window === 'undefined') {
      return false;
    }
    // Established Web Audio and fetch path
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    return typeof fetch === 'function' && typeof AudioCtx === 'function';
  }

  private executeVoiceOutput(
    text: string,
    options: SpeechOptions,
    currentGen: number,
    onEnd: () => void,
    onError: (err: any) => void
  ): void {
    if (this.voiceAdapter) {
      try {
        const res = this.voiceAdapter.speak(text, {
          rate: options.rate,
          pitch: options.pitch,
          onEnd,
          onError
        });
        if (res && typeof (res as Promise<void>).catch === 'function') {
          (res as Promise<void>).catch(onError);
        }
      } catch (err) {
        onError(err);
      }
      return;
    }

    // Default Wingman voice output path via Neural TTS gateway + Web Audio
    if (typeof window === 'undefined') {
      onError(new Error('Window unavailable'));
      return;
    }

    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) {
      onError(new Error('AudioContext unavailable'));
      return;
    }

    const gatewayUrl = (window as any).__WINGMAN_GATEWAY_URL__
      || (import.meta as any).env?.VITE_GATEWAY_URL
      || 'http://localhost:8082';
    const activeVoiceName = (typeof localStorage !== 'undefined' && localStorage.getItem('wingman_voice_name')) || 'en-US-AvaNeural';

    fetch(`${gatewayUrl.replace(/\/$/, '')}/v1/azure/speech/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, voiceName: activeVoiceName })
    })
      .then(res => {
        if (!res.ok) throw new Error(`TTS HTTP Error ${res.status}`);
        return res.arrayBuffer();
      })
      .then(async arrayBuffer => {
        if (this.generation !== currentGen) return;

        const targetCtx = (window as any).wingmanVoice?.audioContext || new AudioCtx({ sampleRate: 24000 });
        if (targetCtx.state !== 'running') {
          await targetCtx.resume();
        }
        if (this.generation !== currentGen) return;

        const audioBuffer = await targetCtx.decodeAudioData(arrayBuffer);
        if (this.generation !== currentGen) return;

        const source = targetCtx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(targetCtx.destination);
        this.activeAudioSource = source;

        source.onended = () => {
          if (this.activeAudioSource === source) {
            this.activeAudioSource = null;
          }
          onEnd();
        };

        source.start(0);
      })
      .catch(err => {
        onError(err);
      });
  }

  private dispatchVoiceUnavailable(reason: string, text: string, error?: any): void {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(new CustomEvent('WINGMAN_VOICE_UNAVAILABLE', {
        detail: { reason, text, error }
      }));
    } catch (e) {}
  }

  /**
   * Updates hidden ARIA live region for screen-reader accessibility
   */
  private updateScreenReaderLiveRegion(text: string): void {
    if (typeof document === 'undefined') return;
    try {
      let liveRegion = document.getElementById('wingman-sr-speech-announcement');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'wingman-sr-speech-announcement';
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.style.position = 'absolute';
        liveRegion.style.width = '1px';
        liveRegion.style.height = '1px';
        liveRegion.style.margin = '-1px';
        liveRegion.style.padding = '0';
        liveRegion.style.overflow = 'hidden';
        liveRegion.style.clip = 'rect(0, 0, 0, 0)';
        liveRegion.style.border = '0';
        document.body.appendChild(liveRegion);
      }
      liveRegion.textContent = text;
    } catch (e) {}
  }
}

export const speechAuthority = SpeechAuthority.getInstance();
